import { ref } from 'vue'
import { useConfirm, useToast } from 'catalyst-vue'
import type { Library } from '../db/schema'
import { useLibraryStore } from '../stores/library'
import { errorMessage } from './fs-access'
import { relocationSummary } from './comic-relink'

/**
 * 來源重新指向的完整流程：選資料夾 → 逐本探測 → 確認 → 寫入。
 *
 * 管理來源對話框的每一列與卡片選單（單本匯入的作品，其資料夾就是來源）共用，兩處的
 * 文案與確認因此不會分岔。選擇器必須在點擊的同步呼叫堆疊內開啟，所以 `relocate` 在呼叫
 * `previewRelocation` 之前不插入任何 await；呼叫端也必須同步呼叫它。
 */
export function useSourceRelocation(onError: (message: string) => void) {
  const store = useLibraryStore()
  const { confirm } = useConfirm()
  const toast = useToast()
  /** 正在核對新位置的那個來源；探測期間呼叫端可據此顯示進行中。 */
  const relocating = ref<string>()

  async function relocate(source: Library): Promise<void> {
    relocating.value = source.id
    let preview: Awaited<ReturnType<typeof store.previewRelocation>>
    try { preview = await store.previewRelocation(source.id) }
    catch (cause) { onError(errorMessage(cause)); return }
    finally { relocating.value = undefined }
    if (!preview) return
    if (preview === 'duplicate') {
      toast.warning('這個資料夾已經是書庫的來源了', { description: '沒有更換指向。' })
      return
    }
    const chosen = preview
    await confirm({
      title: `把「${source.name}」指向「${chosen.handle.name}」？`,
      description: relocationSummary(chosen),
      confirmLabel: '重新指向', pendingLabel: '正在重新指向…', errorMessage,
      onConfirm: async () => {
        await store.relocateSource(chosen)
        toast.success(`「${chosen.handle.name}」已成為這個來源的位置`, {
          description: chosen.total ? `找到 ${chosen.found}／${chosen.total} 本作品。` : undefined,
        })
      },
    })
  }

  return { relocating, relocate }
}
